/**
 * One way of reading every deal, whoever started it and whatever its type:
 *
 *   Sent → Agreed → Do the work → Complete → Paid
 *
 * Built from the collaboration record the API already returns (status, milestones, payment).
 * The "Do the work" steps differ by deal type; everything else is the same for all deals.
 */

export type DealType = 'content' | 'gifting' | 'appearance' | 'licensing'
export type StageId = 'sent' | 'agreed' | 'work' | 'complete' | 'paid'

export const STAGES: { id: StageId; label: string }[] = [
  { id: 'sent', label: 'Sent' },
  { id: 'agreed', label: 'Agreed' },
  { id: 'work', label: 'Do the work' },
  { id: 'complete', label: 'Complete' },
  { id: 'paid', label: 'Paid' },
]

export const DEAL_TYPE_LABELS: Record<DealType, string> = {
  content: 'Content',
  gifting: 'Gifting',
  appearance: 'Appearance',
  licensing: 'Licensing',
}

/** Deals at or above this value need NIL Go and a report to the athlete's school */
export const NIL_REPORTING_THRESHOLD = 600

type Milestone = {
  id: string | number
  slug?: string | null
  type_slug?: string | null
  name?: string
  status: string
  dueDate?: string | null
  requires_approval?: boolean
  submissions?: { status?: string }[]
}

export type DealLike = {
  id: string
  status: string // offered | pending_brand | accepted | rejected | expired
  dealType?: DealType | string | null
  paymentAmount?: number | null
  paymentStatus?: string | null
  licensingEnabled?: boolean | null
  complianceNeeded?: boolean | null
  nilGoStatus?: string | null
  institutionalDisclosureStatus?: string | null
  milestones?: Milestone[]
  invoiceStatus?: string | null
  createdByAthlete?: boolean
}

const slugOf = (m: Milestone) => m.slug ?? m.type_slug ?? ''
const isDone = (m: Milestone) => m.status === 'approved' || m.status === 'completed'
const awaitingReview = (m: Milestone) =>
  m.status === 'submitted' ||
  (m.submissions ?? []).some((s) => s.status === 'pending_review') ||
  false

/** The work milestones, in order, excluding terms and licensing add-ons */
export const workSteps = (deal: DealLike) =>
  (deal.milestones ?? [])
    .filter((m) => !['agree_on_terms', 'license_content'].includes(slugOf(m)))
    .sort((a, b) => ((a as any).sequence_order ?? 0) - ((b as any).sequence_order ?? 0))

export const dealTypeOf = (deal: DealLike): DealType =>
  (['content', 'gifting', 'appearance', 'licensing'] as DealType[]).includes(
    deal.dealType as DealType,
  )
    ? (deal.dealType as DealType)
    : 'content'

export type StageState = {
  stage: StageId
  /** Index of the current stage in STAGES, for the bar */
  index: number
  /** The deal ended without happening */
  ended: 'declined' | 'expired' | 'withdrawn' | null
  /** Who the deal is waiting on right now */
  waitingOn: 'athlete' | 'brand' | 'nobody'
  /** The current work step, when in the work stage */
  currentStep: Milestone | null
  stepsDone: number
  stepsTotal: number
}

export function stageOf(deal: DealLike): StageState {
  const steps = workSteps(deal)
  const stepsDone = steps.filter(isDone).length
  const base = { currentStep: null as Milestone | null, stepsDone, stepsTotal: steps.length }

  if (deal.status === 'rejected')
    return { ...base, stage: 'sent', index: 0, ended: 'declined', waitingOn: 'nobody' }
  if (deal.status === 'expired')
    return { ...base, stage: 'sent', index: 0, ended: 'expired', waitingOn: 'nobody' }
  if (deal.status === 'withdrawn')
    return { ...base, stage: 'sent', index: 0, ended: 'withdrawn', waitingOn: 'nobody' }
  if (deal.status === 'offered')
    return { ...base, stage: 'sent', index: 0, ended: null, waitingOn: 'athlete' }
  if (deal.status === 'pending_brand')
    return { ...base, stage: 'sent', index: 0, ended: null, waitingOn: 'brand' }

  // accepted
  if (deal.paymentStatus === 'paid')
    return { ...base, stage: 'paid', index: 4, ended: null, waitingOn: 'nobody' }
  if (steps.length > 0 && stepsDone === steps.length)
    return { ...base, stage: 'complete', index: 3, ended: null, waitingOn: 'brand' }

  const current = steps.find((m) => !isDone(m)) ?? null
  if (!current) return { ...base, stage: 'agreed', index: 1, ended: null, waitingOn: 'nobody' }

  const brandStep = ['product_shipped', 'prepare_appearance', 'brand_confirms', 'brand_approves']
  const waitingOn: StageState['waitingOn'] =
    awaitingReview(current) || brandStep.includes(slugOf(current)) ? 'brand' : 'athlete'
  return { ...base, stage: 'work', index: 2, ended: null, waitingOn, currentStep: current }
}

// ── Compliance ────────────────────────────────────────────────────────────────

export type ComplianceItem = {
  id: string
  label: string
  /** 'auto' Athios handles it; 'done' finished; 'waiting' on an event; 'needs_you' the athlete must act */
  state: 'auto' | 'done' | 'waiting' | 'needs_you'
  detail?: string
}

export const needsNilReporting = (deal: DealLike) =>
  !!deal.complianceNeeded || (deal.paymentAmount ?? 0) >= NIL_REPORTING_THRESHOLD

/** The "Athios handles" card: what is automatic, what is done, what the athlete must do */
export function complianceItems(deal: DealLike): ComplianceItem[] {
  const s = stageOf(deal)
  const agreed = s.index >= 1
  const items: ComplianceItem[] = [
    {
      id: 'terms',
      label: 'Terms signed',
      state: agreed ? 'done' : 'waiting',
      detail: agreed ? 'Done' : 'On agreement',
    },
    {
      id: 'funds',
      label: 'Brand funds held first',
      state: agreed ? 'done' : 'waiting',
      detail: agreed ? 'Captured' : 'On agreement',
    },
  ]
  if (needsNilReporting(deal)) {
    const nilDone = deal.nilGoStatus === 'confirmed' || deal.nilGoStatus === 'not_required'
    const schoolDone =
      deal.institutionalDisclosureStatus === 'submitted' ||
      deal.institutionalDisclosureStatus === 'acknowledged' ||
      deal.institutionalDisclosureStatus === 'not_required'
    items.push({
      id: 'nil_go',
      label: 'NIL Go submission',
      state: nilDone ? 'done' : agreed ? 'needs_you' : 'waiting',
      detail: nilDone ? 'Confirmed' : agreed ? 'Needs you' : 'After agreement',
    })
    items.push({
      id: 'school',
      label: 'Report to your school',
      state: schoolDone ? 'done' : nilDone ? 'auto' : 'waiting',
      detail: schoolDone ? 'Sent' : 'With NIL Go',
    })
  }
  const posted = s.index >= 3
  items.push({
    id: 'ad',
    label: '#ad disclosure check',
    state: posted ? 'done' : 'waiting',
    detail: posted ? 'Checked' : 'After post',
  })
  items.push({
    id: 'payment',
    label: 'Payment record and tax form',
    state: s.stage === 'paid' ? 'done' : 'auto',
    detail: s.stage === 'paid' ? 'On file' : 'Auto',
  })
  return items
}

/** True when the athlete must file NIL Go before the work can start */
export const nilGoBlocksWork = (deal: DealLike) =>
  needsNilReporting(deal) &&
  stageOf(deal).index >= 1 &&
  !['confirmed', 'not_required'].includes(deal.nilGoStatus ?? '')

/** One line that says what this deal is waiting for */
export function dealStatusLine(deal: DealLike, s: StageState): string {
  if (s.ended === 'declined') return 'Declined'
  if (s.ended === 'expired') return 'Offer expired'
  if (s.ended === 'withdrawn') return 'Offer withdrawn'
  switch (s.stage) {
    case 'sent':
      return s.waitingOn === 'athlete' ? 'Offer for you. Reply now' : 'Waiting on the brand'
    case 'agreed':
      return 'Agreed. Work starts soon'
    case 'work': {
      const name = (s.currentStep as any)?.name ?? 'Next step'
      return s.waitingOn === 'athlete' ? `Your turn: ${name}` : `Waiting on the brand: ${name}`
    }
    case 'complete':
      return 'Done. Payout on its way'
    case 'paid':
      return 'Paid'
  }
}
